import React, { useState, useEffect } from "react";
import { GripHandle } from "@/components/ui/grip-handle";
import { Button, Icon, ScrollArea } from "@elaniintc/goes-design-system";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@radix-ui/react-dropdown-menu";
import { DndContext, closestCenter, type DragEndEvent } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface Question {
  id: string;
  question: string;
  answer: string;
  category: string;
}

interface Section {
  section: string;
  questions: Question[];
}

interface SortDropdownProps {
  /** Lista inicial de secciones */
  sections: Section[];
  /** Callback tras reordenar: recibe el nuevo arreglo ordenado */
  onReorder?: (orderedSections: Section[]) => void;
}

export const SortDropdown: React.FC<SortDropdownProps> = ({
  sections,
  onReorder,
}) => {
  const [ordered, setOrdered] = useState<Section[]>(sections);

  // Sincroniza si cambian las props.sections
  useEffect(() => {
    setOrdered(sections);
  }, [sections]);

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;

    const oldIndex = ordered.findIndex((s) => s.section === active.id);
    const newIndex = ordered.findIndex((s) => s.section === over.id);

    // No permitir soltar antes de la primera
    if (newIndex === 0) return;

    // Calcula nuevo orden, actualiza estado y dispara callback
    const newOrdered = arrayMove(ordered, oldIndex, newIndex);
    setOrdered(newOrdered);
    onReorder?.(newOrdered);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="tertiary" className="shadow flex items-center gap-1">
          Reordenar{" "}
          <Icon type="far" name="arrows-up-down" className="text-current" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        data-testid="dropdown-content"
        className="w-[325px] z-50 rounded-lg border border-neutral p-1 bg-neutral-object text-body font-normal"
        side="bottom"
        align="end"
        sideOffset={4}
      >
        <DndContext
          data-testid="dnd-context"
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <ScrollArea className="h-[264px]">
            <SortableContext
              data-testid="sortable-context"
              data-strategy="verticalListSortingStrategy"
              data-items={JSON.stringify(ordered.map((s) => s.section))}
              strategy={verticalListSortingStrategy}
              items={ordered.map((s) => s.section)}
            >
              {ordered.map((sec, idx) => (
                <SortableSection
                  key={sec.section}
                  sec={sec}
                  disabled={idx === 0}
                />
              ))}
            </SortableContext>
          </ScrollArea>
        </DndContext>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface SortableSectionProps {
  sec: Section;
  disabled: boolean;
}
const SortableSection: React.FC<SortableSectionProps> = ({ sec, disabled }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sec.section,
    disabled,
    // Desactiva animaciones de layout (height/width)
    animateLayoutChanges: () => false,
  });

  const style = {
    transform: transform ? CSS.Translate.toString(transform) : undefined,
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  const containerClasses = disabled
    ? "flex items-center space-x-4 px-3 py-2 rounded-md bg-neutral-object-accent-silent text-neutral-400 cursor-not-allowed"
    : "flex items-center space-x-4 px-3 py-2 rounded-md hover:bg-neutral-object-accent-quiet cursor-pointer";

  return (
    <div ref={setNodeRef} style={style} className={containerClasses}>
      <div {...listeners} {...attributes}>
        <GripHandle className={disabled ? "opacity-50" : undefined} />
      </div>
      <span>{sec.section}</span>
    </div>
  );
};
